import * as vp from 'vopay';

import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import vopayApi from '@/services/api.config';

import type { AccountsHandlers, DialogueHandlerProps, TransactionDialogueHandlers } from '../types';
import type { TransactionsCtx } from './TransactionsContext';

import TransactionsContext from './TransactionsContext';
import TransactionsList from './TransactionsList';
import TransactionsDialogue from './TransactionsDialogue';
import usePostTransactionRefund from '@/hooks/useApi/usePostTransactionRefund';
import useFetchAccountTransactions from '@/hooks/useApi/useFetchAccountTransactions';
import { useDataFetchIndicator } from '@/hooks';

type TransactionsContainerProps = {
  details: vp.AccountDetailsMap;
  handlers: AccountsHandlers;
  defaultTransactions: vp.AccountTransactionsMap;
};

type FC = FunctionComponent<TransactionsContainerProps>;

const TransactionsContainer: FC = ({
  details,
  handlers: accountsHandlers,
  defaultTransactions,
}: TransactionsContainerProps) => {
  /* 

    ▼ setup initial state for transactions container ▼ 

  */
  const history = useHistory();
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const [transactions, setTransactions] = useState<vp.AccountTransactionsMap>({});
  const [transactionsSearchData, setTransactionsSearchData] = useState<vp.AccountTransactionsRequest>();
  const [isSubmittingSearch, setIsSubmittingSearch] = useState(false);
  const [refundSubmissions, setRefundSubmissions] = useState<{ [transactionId: string]: boolean }>({});

  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isRefreshTransactions, setIsRefreshTransactions] = useState(false);
  const [transactionsSearchStatus, setTransactionsSearchStatus] = useState<boolean | null>(null);
  const [transactionsSearchError, setTransactionsSearchError] = useState<string>('');
  const [showTransactionsSearchErrorAlert, setShowTransactionsSearchErrorAlert] = useState(false);

  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [transactionRefundError, setTransactionRefundError] = useState('');
  const [showTransactionsRefundErrorAlert, setShowTransactionsRefundErrorAlert] = useState(false);
  const [showTransactionsRefundSuccessAlert, setShowTransactionsRefundSuccessAlert] = useState(false);

  const transactionsContext: TransactionsCtx = {
    transactionsSearchStatus,
    transactionsSearchError,
    transactionRefundError,
    showTransactionsSearchErrorAlert,
    showTransactionsRefundErrorAlert,
    showTransactionsRefundSuccessAlert,
  };

  const { accountId } = useParams<{ accountId: string }>();

  const hasDefaultTransactions = defaultTransactions && defaultTransactions[accountId];
  const [shouldFetchData, setShouldFetchData] = useState(!hasDefaultTransactions);

  const [transactionRefund, postTransactionRefund, resetTransactionRefund] = usePostTransactionRefund({
    bearer: details[accountId].token,
  });
  const [accountTransactionsList, fetchAccountTransactions, resetAccountTransactions] = useFetchAccountTransactions({
    bearer: details[accountId].token,
  });

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp] | undefined>();
  const [areDatesValid, setAreDatesValid] = useState(false);

  useEffect(() => {
    if (timeRange) {
      const FromDate = moment(timeRange?.[0]).format('YYYY-MM-DD');
      const ToDate = moment(timeRange?.[1]).format('YYYY-MM-DD');

      setStartDate(FromDate);
      setEndDate(ToDate);
    }
  }, [timeRange]);

  useEffect(() => {
    setAreDatesValid(isDateValid(startDate) && isDateValid(endDate));
  }, [startDate, endDate]);

  /* 

    ▼ initial call and handling of transaction data ▼

  */

  useEffect(() => {
    // if it is the first load of the page and we have saved transactions, get them from that state
    if (!shouldFetchData && defaultTransactions[accountId] && !Object.values(transactions).length) {
      setTransactions((prevState: vp.AccountTransactionsMap) => ({
        ...prevState,
        [accountId]: defaultTransactions[accountId],
      }));

      setShouldFetchData(false);
      return () => {};
    }
    if (!shouldFetchData && !isSubmittingSearch && !isExportingCSV) {
      return () => {};
    }

    const getTransactions = () => {
      // default values when no search has been done
      const currentDateTime = moment().format('YYYY-MM-DD');
      const timeDifference = moment().subtract(1, 'week').format('YYYY-MM-DD');

      const defaultValues: vp.AccountTransactionsRequest = {
        StartDateTime: timeDifference,
        EndDateTime: currentDateTime,
        Currency: 'CAD',
      };

      // use values from the search form or default values
      const accountTransactionRequest = transactionsSearchData
        ? {
            ...transactionsSearchData,
            TransactionType:
              transactionsSearchData.TransactionType === 'All' ? undefined : transactionsSearchData.TransactionType,
            ExportCSV: isExportingCSV ? true : false,
          }
        : defaultValues;

      // add the time manually
      accountTransactionRequest.StartDateTime = startDate ? startDate : timeDifference;
      accountTransactionRequest.EndDateTime = endDate ? endDate : currentDateTime;

      if (!hasInvalidEndDate) {
        setIsRefreshTransactions(true);
        fetchAccountTransactions(accountTransactionRequest);
      } else {
        setShouldShowEndDateError(true);
      }
    };

    return getTransactions();
  }, [isSubmittingSearch, isExportingCSV, shouldFetchData, details]);

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    // if it is the first load of the page and we have saved transactions, get them from that state

    const proccessTransactionsData = () => {
      if (isRefreshTransactions) {
        const { Transactions, CSVFile, Success, ErrorMessage } = accountTransactionsList;
        if (Transactions) {
          const transactionList = Object.values(Transactions);
          setTransactions((prevState: vp.AccountTransactionsMap) => ({
            ...prevState,
            [accountId]: transactionList,
          }));
          resetAccountTransactions();
          setIsRefreshTransactions(false);
          // save state in an upper level so transactions are not fetch all the time
          if (!defaultTransactions || !defaultTransactions[accountId]) {
            accountsHandlers.onTransactionsFetch(accountId, transactionList);
          }
        }

        // gets CSV base64 content and triggers a download
        if (CSVFile) {
          const downloadLink = document.createElement('a');
          const fileName = 'account_transactions.csv';

          downloadLink.href = CSVFile;
          downloadLink.download = fileName;
          downloadLink.click();
        }

        setIsExportingCSV(false);
        setIsSubmittingSearch(false);
        setShouldFetchData(false);
        setDataFetchingInfo(false);
        setTransactionsSearchStatus(Success);
        setTransactionsSearchError(ErrorMessage);
      }
    };

    void proccessTransactionsData();
  }, [accountTransactionsList]);

  useEffect(() => {
    if (transactionsSearchStatus === false) {
      setShowTransactionsSearchErrorAlert(true);
    }
  }, [transactionsSearchStatus]);

  useEffect(() => {
    const { success, errorMessage, transactionId } = transactionRefund;

    if (success !== null) {
      setRefundSubmissions((prevState) => ({ ...prevState, [transactionId]: false }));
      resetTransactionRefund();

      if (errorMessage) setTransactionRefundError(`${transactionId}: ${errorMessage}`);
      if (success === false) setShowTransactionsRefundErrorAlert(true);
      if (success === true) setShowTransactionsRefundSuccessAlert(true);
    }
  }, [transactionRefund]);

  const handlers = {
    onTransactionRefund: (id: string) => {
      setRefundSubmissions((prevState) => ({ ...prevState, [id]: true }));
      postTransactionRefund(id);
    },

    onTransactionsSearch: (values: vp.AccountTransactionsRequest) => {
      if (isExportingCSV) {
        setTransactionsSearchData({ ...values, ExportCSV: true });
      } else {
        setTransactionsSearchData(values);
        setIsSubmittingSearch(true);
      }
    },

    handleDialogue: (handlerProps: DialogueHandlerProps<TransactionDialogueHandlers>) => {
      const { type, state, action = 'default', hasNavigation } = handlerProps;

      const dialogueHandlers: TransactionDialogueHandlers = {
        search: {
          success: {
            default: () => {},
          },
          fail: {
            default: () => {
              setShowTransactionsSearchErrorAlert(false);
              setTransactionsSearchStatus(null);
            },
          },
        },
        refund: {
          success: {
            refresh: () => {
              setShouldFetchData(true);
              setShowTransactionsRefundSuccessAlert(false);
            },
            default: () => {
              setShowTransactionsRefundSuccessAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowTransactionsRefundErrorAlert(false);
              setTransactionRefundError('');
            },
          },
        },
      };

      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();

      if (hasNavigation) history.push('/accounts');
    },

    handleDate: (values: [vp.Timestamp, vp.Timestamp] | undefined) => {
      setTimeRange(values);
    },

    exportCSV: (): void => {
      setIsExportingCSV(true);
    },
  };

  return (
    <TransactionsContext.Provider value={transactionsContext}>
      <TransactionsList
        areDatesValid={areDatesValid}
        handlers={handlers}
        transactions={transactions}
        isExportingCSV={isExportingCSV}
        refundSubmissions={refundSubmissions}
        hasInvalidEndDate={hasInvalidEndDate}
        shouldShowEndDateError={shouldShowEndDateError}
        setShouldShowEndDateError={setShouldShowEndDateError}
      />
      <TransactionsDialogue handleDialogue={handlers.handleDialogue} />
    </TransactionsContext.Provider>
  );

  function isDateValid(date: string) {
    if (!date) return true;

    const [, mm, dd] = date.split('-');

    const m = parseInt(mm, 10);
    const d = parseInt(dd, 10);

    const isValidMonth = m > 0 && m <= 12;
    const isValidDay = d > 0 && d <= 31 && dd.length === 2;

    return isValidMonth && isValidDay;
  }
};

export default vopayApi.withApiComponent<TransactionsContainerProps>(TransactionsContainer);
